// ── Studio Constants ──────────────────────────────────────────────────────────

export const PILLARS = [
  {
    id: 'system-decision',
    tag: '01',
    label: 'System Decision',
    days: 'Mon · Sun',
    color: '#d4582d',
    rgb: '201,168,76',
  },
  {
    id: 'build-log',
    tag: '02',
    label: 'Build Log',
    days: 'Tue · Fri',
    color: '#e8985a',
    rgb: '91,155,213',
  },
  {
    id: 'deep-dive',
    tag: '03',
    label: 'Technical Deep Dive',
    days: 'Wednesday',
    color: '#a07a55',
    rgb: '149,128,212',
  },
  {
    id: 'engineering-standards',
    tag: '04',
    label: 'Engineering Standards',
    days: 'Thursday',
    color: '#4a9b6f',
    rgb: '74,155,111',
  },
  {
    id: 'ai-position',
    tag: '05',
    label: 'AI Position',
    days: 'Saturday',
    color: '#a4b6e0',
    rgb: '164,182,224',
  },
  {
    id: 'field-notes',
    tag: '08',
    label: 'Field Notes',
    days: 'Sunday',
    color: '#9b8a6e',
    rgb: '155,138,110',
  },
]

export const DAY_PILLARS = {
  0: 'field-notes',
  1: 'system-decision',
  2: 'build-log',
  3: 'deep-dive',
  4: 'engineering-standards',
  5: 'build-log',
  6: 'ai-position',
}

export const PHASES = [
  {
    range: 'Days 1–30',
    label: 'Establish Rhythm',
    goals: [
      '30 posts per platform',
      'Clear voice established',
      'Bios and pinned posts repositioned',
      'Focus on Build Log and System Decisions',
    ],
  },
  {
    range: 'Days 31–60',
    label: 'Add Depth',
    goals: [
      'First inbound messages from founders/engineers',
      'Wednesday long-form (Technical Deep Dive) introduced',
      'Start engaging in technical conversations',
      'Cross-reference projects in posts (GitHub, portfolio)',
    ],
  },
  {
    range: 'Days 61–90',
    label: 'Authority Layer',
    goals: [
      '2–3 real conversations with potential contract clients from social alone',
      'Engineering Standards posts become most opinionated content',
      'Profile and content working as a unified system',
      'Begin converting engaged followers into direct conversations',
    ],
  },
]

export function getPillar(id) {
  return PILLARS.find((p) => p.id === id) || PILLARS[0]
}

export function getContentDisplayLabel(contentType) {
  const key =
    typeof contentType === 'string' ? contentType : contentType?.key || ''
  if (key === 'newsletter-issue') return 'Newsletter / Substack'
  if (typeof contentType === 'object' && contentType?.label) {
    return contentType.label
  }
  return key
}

export function fmtDateTime(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function daysSince(isoStr) {
  if (!isoStr) return 0
  return Math.floor((Date.now() - new Date(isoStr)) / 86400000)
}

export const RUNTIME_CHECK_LABELS = {
  'linkedin-credentials': 'LinkedIn Credentials',
  'x-credentials': 'X Credentials',
  'github-credentials': 'GitHub Credentials',
  'notion-credentials': 'Notion Credentials',
  'anthropic-generate': 'Anthropic Generation',
  'notion-post-roundtrip': 'Notion Post Round-Trip',
  'notion-brief-roundtrip': 'Notion Brief Round-Trip',
  'linkedin-post-smoke': 'LinkedIn Post Smoke',
  'x-post-smoke': 'X Post Smoke',
  'x-thread-smoke': 'X Thread Smoke',
}

export function formatVerificationCredentialSource(source) {
  if (source === 'env') return 'Env'
  if (source === 'settings') return 'Settings'
  if (source === 'missing') return 'Missing'
  return 'N/A'
}

export function getVerificationStatusTone(status) {
  if (status === 'success') return 'success'
  if (status === 'failure') return 'error'
  return 'planning'
}

export function getVerificationSummary(verification) {
  const metadata =
    verification?.metadata && typeof verification.metadata === 'object'
      ? verification.metadata
      : {}
  const identity =
    metadata.identity && typeof metadata.identity === 'object'
      ? metadata.identity
      : {}

  if (identity.username) {
    return `@${identity.username}`
  }
  if (identity.login) {
    return `@${identity.login}`
  }
  if (identity.name) {
    return identity.name
  }
  if (identity.workspaceName) {
    return identity.workspaceName
  }
  if (metadata.generatedChars) {
    return `${metadata.generatedChars} chars generated`
  }
  if (metadata.readbackSuccess != null) {
    return [
      metadata.readbackSuccess ? 'read-back ok' : 'read-back failed',
      metadata.cleanupSuccess ? 'cleanup ok' : 'cleanup failed',
    ].join(' · ')
  }
  if (verification?.errorCode) return verification.errorCode
  return verification?.status === 'success' ? 'Live check passed' : 'No run yet'
}

// Wave Q.2 — sidebar grouping refactored to mirror the studio's
// authority lifecycle (Signal → Capture → Truth → Research → Proof
// → Draft → Queue → Publish → Measure → Learn). The previous Daily/
// Workshop/Control taxonomy collapsed too many distinct stages into
// one group and made it harder for the operator to see *where* in
// the loop they are. Six groups read top-to-bottom:
//
//   Command   — Lancer (operator console), Dashboard (overview)
//   Input     — Capture (ideas / signal intake)
//   Knowledge — Truth, Research, Proof, Reports
//   Output    — Compose, Posts, Calendar
//   Feedback  — Insights, Archive
//   Control   — Autonomy, Settings
//
// Routes are unchanged — this is purely a visual reorganization that
// surfaces the lifecycle. Tab IDs stay intact so any deep links,
// dashboard handoffs, or test references keep working.
export const NAV = [
  { id: 'lancer', label: 'Lancer', icon: '◆', group: 'command' },
  { id: 'dashboard', label: 'Dashboard', icon: '▣', group: 'command' },
  { id: 'engagement', label: 'Engagement', icon: '◎', group: 'command' },
  { id: 'ideas', label: 'Capture', icon: '◇', group: 'input' },
  { id: 'truth', label: 'Truth', icon: '⋄', group: 'knowledge' },
  { id: 'research', label: 'Research', icon: '⊞', group: 'knowledge' },
  { id: 'proof', label: 'Proof', icon: '◈', group: 'knowledge' },
  { id: 'reports', label: 'Reports', icon: '◫', group: 'knowledge' },
  { id: 'create', label: 'Compose', icon: '✎', group: 'output' },
  {
    id: 'posts',
    label: 'Posts',
    icon: '☰',
    group: 'output',
    matches: ['posts', 'drafts', 'queue', 'delivered'],
  },
  { id: 'calendar', label: 'Calendar', icon: '◧', group: 'output' },
  { id: 'insights', label: 'Insights', icon: '◐', group: 'feedback' },
  { id: 'archive', label: 'Archive', icon: '⊟', group: 'feedback' },
  { id: 'autonomy', label: 'Autonomy', icon: '◉', group: 'control' },
  { id: 'settings', label: 'Settings', icon: '⚙', group: 'control' },
]

export const NAV_GROUPS = [
  { id: 'command', label: 'Command' },
  { id: 'input', label: 'Input' },
  { id: 'knowledge', label: 'Knowledge' },
  { id: 'output', label: 'Output' },
  { id: 'feedback', label: 'Feedback' },
  { id: 'control', label: 'Control' },
]

// Maps the consolidated Posts sub-tabs back to the legacy tab IDs the
// rest of the app already understands. Dashboard handoffs that target
// drafts/queue/delivered keep working unchanged.
export const POSTS_TAB_IDS = ['drafts', 'queue', 'delivered']

export const TOPBAR = {
  lancer: ['Lancer', 'Your scriba'],
  dashboard: ['Dashboard', 'Command center'],
  create: ['Dashboard', 'Compose · draft editor'],
  ideas: ['Capture', 'Source evidence and authority review'],
  truth: ['Truth', 'Projects, positions, and canonical stances'],
  posts: ['Posts', 'Drafts, queue, and delivery history'],
  drafts: ['Drafts', 'Focused editing and review'],
  queue: ['Queue', 'Scheduled drafts and publish queue'],
  delivered: ['Delivered', 'Live and manual delivery history'],
  insights: ['Insights', 'Engagement, decisions, and spend'],
  research: ['Research', 'Briefs for stronger public proof'],
  proof: ['Proof', 'Reusable evidence for Ignazio’s authority'],
  reports: ['Reports', 'Strategic proof artifacts'],
  archive: ['Archive', 'Durable restore vault'],
  calendar: ['Calendar', 'Planning calendar'],
  progress: ['90-day Arc', 'Progress and cadence'],
  autonomy: ['Autonomy', 'Kill switch, policies, cooldowns, calibration'],
  settings: ['Settings', 'Infrastructure and credentials'],
}

export const OAUTH_STEPS = `1. Go to https://www.linkedin.com/developers/apps
2. Create or open your LinkedIn app and enable "Share on LinkedIn" plus "Sign In with LinkedIn"
3. Set the callback URL to LINKEDIN_REDIRECT_URI if you use it, otherwise APP_BASE_URL/auth/linkedin/callback
4. Local default: http://localhost:4000/auth/linkedin/callback
5. Scopes requested by the studio: w_member_social, r_liteprofile, openid, profile, email
6. Start the auth flow from the studio proxy route: /auth/linkedin/start
7. Complete the LinkedIn consent screen and let the proxy handle the callback and token exchange
8. Copy LINKEDIN_ACCESS_TOKEN and LINKEDIN_AUTHOR_ID from the callback page into Settings or .env
9. If the callback fails, confirm the app callback URL matches the runtime value exactly`

export const TW_OAUTH_STEPS = `1. Go to https://developer.twitter.com/en/portal/dashboard
2. Create or open your Project + App and enable OAuth 2.0 for a Web App
3. Set the callback URL to APP_BASE_URL/auth/twitter/callback
4. Local default: http://localhost:4000/auth/twitter/callback
5. Scopes requested by the studio: tweet.read, tweet.write, users.read, follows.read, follows.write, like.read, like.write, dm.read, dm.write, offline.access
6. Start the auth flow from the studio proxy route: /auth/twitter/start
7. Complete the X consent screen and let the proxy handle PKCE, callback validation, and token exchange
8. Copy TWITTER_ACCESS_TOKEN from the callback page into Settings or .env
9. If the callback fails, confirm APP_BASE_URL and the app callback URL match exactly`
